import { MockServiceStore } from "./MockServiceStore";
import { mockStructureCastStructures } from "./structurecast-structures.mock";

/**
 * StructureCast - Component (a piece within a structure)
 *
 * Mid-State Concrete Products — Heritage Ranch WRRF sanitary sewer manhole components:
 *   base      = MH48B##H — 48" Ext Base w/ integral shaft, invert & Press-Seal boots
 *   riser     = MH48S{12,24,36,48} — 48" dia MH riser/shaft, height varies
 *   cone      = MH48CE{18,24,36} — 48" dia x 24" dia Eccentric Cone, height varies
 *   flat_top  = MHGR2402 / MHGR2403 — 24" dia Grade Ring (2" or 3" thick), sump position
 */
export interface StructureCastComponent {
  id: string;
  structureId: string;
  controlNumber: string;
  componentType: "base" | "riser" | "cone" | "flat_top";
  heightFeet: number;
  heightInches: number;
  heightFraction: string;
  length: number;
  width: number;
  weight: number;
  cubicYards: number;
  sortOrder: number;
  status: "pending" | "scheduled" | "cast" | "cured" | "shipped";
  scheduledDate: string;
  actualCastDate: string;
  castingGroupId: string;
  castingDayId: string;
  moldPosition: number | "sump";
  createdAt: string;
  updatedAt: string;
}

interface CompDef {
  type: "base" | "riser" | "cone" | "flat_top";
  controlNumber: string;
  cy: number;
  weightLbs: number;
  heightIn: number;
}

const structureComponents: CompDef[][] = [
  // 0: SSMH-01 (102") — base + 48" riser + 36" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B30H-01", cy: 1.45, weightLbs: 5800, heightIn: 30 },
    { type: "riser", controlNumber: "MH48S48-01", cy: 0.78, weightLbs: 3150, heightIn: 48 },
    { type: "cone", controlNumber: "MH48CE36-01", cy: 0.74, weightLbs: 2960, heightIn: 36 },
    { type: "flat_top", controlNumber: "MHGR2403-01", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 1: SSMH-02 (90") — base + 36" riser + 36" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B30H-02", cy: 1.45, weightLbs: 5800, heightIn: 30 },
    { type: "riser", controlNumber: "MH48S36-01", cy: 0.59, weightLbs: 2360, heightIn: 36 },
    { type: "cone", controlNumber: "MH48CE36-02", cy: 0.74, weightLbs: 2960, heightIn: 36 },
    { type: "flat_top", controlNumber: "MHGR2403-02", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 2: SSMH-03 (78") — base + 24" riser + 36" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B30H-03", cy: 1.45, weightLbs: 5800, heightIn: 30 },
    { type: "riser", controlNumber: "MH48S24-01", cy: 0.39, weightLbs: 1575, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE36-03", cy: 0.74, weightLbs: 2960, heightIn: 36 },
    { type: "flat_top", controlNumber: "MHGR2403-03", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 3: SSMH-04 (78") — base + 24" riser + 36" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B30H-04", cy: 1.45, weightLbs: 5800, heightIn: 30 },
    { type: "riser", controlNumber: "MH48S24-02", cy: 0.39, weightLbs: 1575, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE36-04", cy: 0.74, weightLbs: 2960, heightIn: 36 },
    { type: "flat_top", controlNumber: "MHGR2403-04", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 4: SSMH-05 (66") — base + 12" riser + 24" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B24H-05", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "riser", controlNumber: "MH48S12-01", cy: 0.20, weightLbs: 790, heightIn: 12 },
    { type: "cone", controlNumber: "MH48CE24-01", cy: 0.64, weightLbs: 2560, heightIn: 24 },
    { type: "flat_top", controlNumber: "MHGR2403-05", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 5: SSMH-06 (63") — base + 12" riser + 24" cone + 3" GR
  [
    { type: "base", controlNumber: "MH48B24H-06", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "riser", controlNumber: "MH48S12-02", cy: 0.20, weightLbs: 790, heightIn: 12 },
    { type: "cone", controlNumber: "MH48CE24-02", cy: 0.64, weightLbs: 2560, heightIn: 24 },
    { type: "flat_top", controlNumber: "MHGR2403-06", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
  // 6: SSMH-07 (54") — base + 24" cone + 2" GR (no riser)
  [
    { type: "base", controlNumber: "MH48B24H-07", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE24-03", cy: 0.64, weightLbs: 2560, heightIn: 24 },
    { type: "flat_top", controlNumber: "MHGR2402-01", cy: 0.03, weightLbs: 110, heightIn: 2 },
  ],
  // 7: SSMH-08 (54") — base + 24" cone + 2" GR
  [
    { type: "base", controlNumber: "MH48B24H-08", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE24-04", cy: 0.64, weightLbs: 2560, heightIn: 24 },
    { type: "flat_top", controlNumber: "MHGR2402-02", cy: 0.03, weightLbs: 110, heightIn: 2 },
  ],
  // 8: SSMH-09 (54") — base + 24" cone + 2" GR
  [
    { type: "base", controlNumber: "MH48B24H-09", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE24-05", cy: 0.64, weightLbs: 2560, heightIn: 24 },
    { type: "flat_top", controlNumber: "MHGR2402-03", cy: 0.03, weightLbs: 110, heightIn: 2 },
  ],
  // 9: SSMH-10 (54") — base + 18" cone + 3" GR — ALL UNSCHEDULED (added late)
  [
    { type: "base", controlNumber: "MH48B24H-10", cy: 1.45, weightLbs: 5800, heightIn: 24 },
    { type: "cone", controlNumber: "MH48CE18-01", cy: 0.48, weightLbs: 1920, heightIn: 18 },
    { type: "flat_top", controlNumber: "MHGR2403-07", cy: 0.04, weightLbs: 145, heightIn: 3 },
  ],
];

/**
 * Explicit schedule — components assigned to casting group/day/mold positions.
 *
 * Key: `${structureIndex}-${componentIndex}` → { groupNum, dayNum, moldPos }
 * null = unscheduled (shows in Unscheduled Components list)
 *
 * DAILY_CAPACITY_CY = 4.5
 *
 * Group 1 (completed, Jan 19-20):
 *   Day 1: SSMH-01 base + SSMH-02 base                                   = 2.90 CY (64%)
 *   Day 2: SSMH-01 48"riser + SSMH-02 36"riser + 2 cones + 2 GR3         = 2.93 CY (65%)
 * Group 2 (in_progress, Jan 26-28):
 *   Day 1: SSMH-03 base + SSMH-04 base + SSMH-05 base                    = 4.35 CY (97%)
 *   Day 2: SSMH-03 24"riser + SSMH-04 24"riser + SSMH-05 12"riser + 36"cones = 2.46 CY (55%)
 *   Day 3: SSMH-05 24"cone + 3 GR3                                       = 0.76 CY (17%)
 * Group 3 (planned, Feb 2-3):
 *   Day 1: SSMH-06 base + SSMH-07 base + SSMH-06 12"riser               = 3.10 CY (69%)
 *   Day 2: SSMH-06 24"cone + SSMH-07 24"cone + GR3 + GR2                 = 1.35 CY (30%)
 * Group 4 (planned, Feb 9-10):
 *   Day 1: SSMH-08 base + SSMH-09 base                                   = 2.90 CY (64%)
 *   Day 2: 2 cones + 2 GR2                                               = 1.34 CY (30%)
 *
 * Unscheduled: SSMH-10 (all 3 components)
 */
interface ScheduleSlot { groupNum: number; dayNum: number; moldPos: number | "sump" }
const S = (groupNum: number, dayNum: number, moldPos: number | "sump"): ScheduleSlot =>
  ({ groupNum, dayNum, moldPos });

const schedule: Record<string, ScheduleSlot | null> = {
  // ── Group 1: Deep Manholes (completed, Jan 19-20) ─────────────────────
  // Day 1: bases
  "0-0": S(1, 1, 1),       // SSMH-01 base       1.00  (Mold 1)
  "1-0": S(1, 1, 2),       // SSMH-02 base       1.00  (Mold 2)
  // Day 2: shafts, cones, grade rings
  "0-1": S(1, 2, 1),       // SSMH-01 48"shaft   0.85  (Mold 1)
  "1-1": S(1, 2, 2),       // SSMH-02 36"shaft   0.65  (Mold 2)
  "0-2": S(1, 2, 3),       // SSMH-01 36"cone    0.51  (Mold 3)
  "1-2": S(1, 2, 4),       // SSMH-02 36"cone    0.51  (Mold 4)
  "0-3": S(1, 2, "sump"),  // SSMH-01 GR3        0.04  (Sump)
  "1-3": S(1, 2, "sump"),  // SSMH-02 GR3        0.04  (Sump, stacked)

  // ── Group 2: Standard Manholes (in_progress, Jan 26-28) ───────────────
  // Day 1: 3 bases
  "2-0": S(2, 1, 1),       // SSMH-03 base       1.00
  "3-0": S(2, 1, 2),       // SSMH-04 base       1.00
  "4-0": S(2, 1, 3),       // SSMH-05 base       1.00
  // Day 2: shafts + cones
  "2-1": S(2, 2, 1),       // SSMH-03 24"shaft   0.42
  "3-1": S(2, 2, 2),       // SSMH-04 24"shaft   0.42
  "4-1": S(2, 2, 3),       // SSMH-05 12"shaft   0.21
  "2-2": S(2, 2, 4),       // SSMH-03 36"cone    0.51
  "3-2": S(2, 2, 5),       // SSMH-04 36"cone    0.51
  // Day 3: SSMH-05 cone + all grade rings
  "4-2": S(2, 3, 1),       // SSMH-05 24"cone    0.27
  "2-3": S(2, 3, "sump"),  // SSMH-03 GR3        0.04  (Sump)
  "3-3": S(2, 3, "sump"),  // SSMH-04 GR3        0.04  (Sump, stacked)
  "4-3": S(2, 3, "sump"),  // SSMH-05 GR3        0.04  (Sump, stacked)

  // ── Group 3: Mid-Depth Manholes (planned, Feb 2-3) ────────────────────
  // Day 1: bases + 1 shaft
  "5-0": S(3, 1, 1),       // SSMH-06 base       1.00
  "6-0": S(3, 1, 2),       // SSMH-07 base       1.00
  "5-1": S(3, 1, 3),       // SSMH-06 12"shaft   0.21
  // Day 2: cones + grade rings
  "5-2": S(3, 2, 1),       // SSMH-06 24"cone    0.27
  "6-1": S(3, 2, 2),       // SSMH-07 24"cone    0.27
  "5-3": S(3, 2, "sump"),  // SSMH-06 GR3        0.04
  "6-2": S(3, 2, "sump"),  // SSMH-07 GR2        0.03  (Sump, stacked)

  // ── Group 4: Shallow Manholes (planned, Feb 9-10) ─────────────────────
  // Day 1: 2 bases
  "7-0": S(4, 1, 1),       // SSMH-08 base       1.00
  "8-0": S(4, 1, 2),       // SSMH-09 base       1.00
  // Day 2: cones + grade rings
  "7-1": S(4, 2, 1),       // SSMH-08 24"cone    0.27
  "8-1": S(4, 2, 2),       // SSMH-09 24"cone    0.27
  "7-2": S(4, 2, "sump"),  // SSMH-08 GR2        0.03
  "8-2": S(4, 2, "sump"),  // SSMH-09 GR2        0.03  (Sump, stacked)

  // ── SSMH-10: UNSCHEDULED (added late to scope) ────────────────────────
  "9-0": null,             // SSMH-10 base
  "9-1": null,             // SSMH-10 18"cone
  "9-2": null,             // SSMH-10 GR3
};

const groupStartDates: Record<number, string> = {
  1: "2026-01-19",
  2: "2026-01-26",
  3: "2026-02-02",
  4: "2026-02-09",
};

const getScheduledDate = (groupNum: number, dayNum: number): string => {
  const startDate = new Date(groupStartDates[groupNum] + "T00:00:00");
  const cursor = new Date(startDate);
  let weekdayCount = 0;
  const daysToAdd = dayNum - 1;
  while (weekdayCount < daysToAdd) {
    cursor.setDate(cursor.getDate() + 1);
    if (cursor.getDay() !== 0 && cursor.getDay() !== 6) {
      weekdayCount++;
    }
  }
  while (cursor.getDay() === 0 || cursor.getDay() === 6) {
    cursor.setDate(cursor.getDate() + 1);
  }
  return cursor.toISOString().split("T")[0];
};

const getGroupInfo = (structIdx: number): { groupId: string } => {
  if (structIdx <= 1) return { groupId: "sc-cg-1" };
  if (structIdx <= 4) return { groupId: "sc-cg-2" };
  if (structIdx <= 6) return { groupId: "sc-cg-3" };
  // SSMH-08, SSMH-09 → Group 4; SSMH-10 → Group 4 (but unscheduled so groupId never set)
  return { groupId: "sc-cg-4" };
};

/**
 * Generate components for all structures with explicit schedule assignments.
 */
const generateComponents = (): StructureCastComponent[] => {
  const components: StructureCastComponent[] = [];
  let compId = 1;

  for (let structIdx = 0; structIdx < mockStructureCastStructures.length; structIdx++) {
    const struct = mockStructureCastStructures[structIdx];
    const compDefs = structureComponents[structIdx] || [];
    const { groupId } = getGroupInfo(structIdx);

    for (let ci = 0; ci < compDefs.length; ci++) {
      const def = compDefs[ci];
      const key = `${structIdx}-${ci}`;
      const slot = schedule[key];

      const getCompStatus = (): StructureCastComponent["status"] => {
        if (!slot) return "pending";
        if (struct.status === "cast_complete") return "cured";
        if (struct.status === "in_progress") return ci === 0 ? "cast" : "scheduled";
        return "scheduled";
      };

      const compStatus = getCompStatus();
      const isScheduled = slot !== null && slot !== undefined;
      const dayId = isScheduled ? `sc-cd-${slot.groupNum}-${slot.dayNum}` : "";
      const date = isScheduled ? getScheduledDate(slot.groupNum, slot.dayNum) : "";
      const moldPos = isScheduled ? slot.moldPos : (def.type === "flat_top" ? "sump" : 1);

      const heightFeet = Math.floor(def.heightIn / 12);
      const heightInches = def.heightIn % 12;

      components.push({
        id: `sc-comp-${String(compId).padStart(3, "0")}`,
        structureId: struct.id,
        controlNumber: def.controlNumber,
        componentType: def.type,
        heightFeet,
        heightInches,
        heightFraction: "",
        length: struct.overallLength,
        width: struct.overallWidth,
        weight: def.weightLbs,
        cubicYards: def.cy,
        sortOrder: ci + 1,
        status: compStatus,
        scheduledDate: date,
        actualCastDate: compStatus === "cured" || compStatus === "cast" ? date : "",
        castingGroupId: isScheduled ? groupId : "",
        castingDayId: dayId,
        moldPosition: moldPos,
        createdAt: "2025-05-29T00:00:00.000Z",
        updatedAt: "2026-01-19T00:00:00.000Z",
      });
      compId++;
    }
  }

  return components;
};

export const mockStructureCastComponents: StructureCastComponent[] = generateComponents();

export const createMockStructureCastComponentStore = () =>
  new MockServiceStore<StructureCastComponent>({
    mockData: mockStructureCastComponents,
    idField: "id",
    simulateDelay: 200,
    persistKey: "preferred-structurecast-components",
  });

export default mockStructureCastComponents;
